import React, { useState, useRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  FaSearch,
  FaTimes,
  FaCheck,
  FaChevronDown,
  FaFilter,
  FaTrashAlt
} from 'react-icons/fa';
import DataSort from './DataSort';

const extractItemProperty = (itemObject, propertyKeyOrExtractor, fallbackValue = '') => {
  if (!itemObject) return fallbackValue;
  if (typeof propertyKeyOrExtractor === 'function') {
    return propertyKeyOrExtractor(itemObject);
  }
  if (typeof propertyKeyOrExtractor === 'string' && propertyKeyOrExtractor in itemObject) {
    return itemObject[propertyKeyOrExtractor];
  }
  return fallbackValue;
};

const DataFilter = ({
  filterLabel = 'Filter Data',
  title,
  options = [],
  data = [],
  valueKey = 'id',
  labelKey = 'name',
  groupKey = '',
  statusKey = 'is_status',
  selectedValues: controlledSelectedValues,
  defaultSelectedValues = [],
  onSelectionChange,
  onChange,
  onSearchChange,
  onSearch,
  isMultiSelect = true,
  isMulti,
  isSearchable = true,
  searchable,
  searchPlaceholder = 'Search...',
  placeholder,
  showSelectedChips = true,
  showChips,
  className = '',
  extraMetadataKeys = [],
  metaExtraKeys = [],
  // Optional Sort props (backward compatible)
  sortOptions = [],
  sortLabel = 'Sort',
  sortValue: controlledSortValue,
  defaultSortValue = '',
  sortOrder: controlledSortOrder,
  defaultSortOrder = 'asc',
  onSortChange,
  showSortOrderToggle = true,
  // Optional Reset
  showReset = false,
  onReset
}) => {
  const displayTitle = title || filterLabel;
  const dataOptions = options.length > 0 ? options : data;
  const isMultiSelection = isMulti !== undefined ? isMulti : isMultiSelect;
  const isSearchEnabled = searchable !== undefined ? searchable : isSearchable;
  const inputPlaceholder = placeholder || searchPlaceholder;
  const renderChips = showChips !== undefined ? showChips : showSelectedChips;
  const metadataKeysList = metaExtraKeys.length > 0 ? metaExtraKeys : extraMetadataKeys;
  const triggerChangeCallback = onSelectionChange || onChange;
  const triggerSearchCallback = onSearchChange || onSearch;

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (typeof triggerSearchCallback === 'function') {
      triggerSearchCallback(value);
    }
  };

  const handleClearSearchInput = () => {
    setSearchQuery('');
    if (typeof triggerSearchCallback === 'function') {
      triggerSearchCallback('');
    }
  };

  const isControlledSelection = controlledSelectedValues !== undefined;
  const [internalSelectedValues, setInternalSelectedValues] = useState(defaultSelectedValues);
  const activeSelectedValues = isControlledSelection ? controlledSelectedValues : internalSelectedValues;

  const containerRef = useRef(null);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const { selectedItemObjects, selectionMetadata, optionsByIdMap } = useMemo(() => {
    const map = new Map();
    dataOptions.forEach((item) => {
      const val = extractItemProperty(item, valueKey);
      if (val !== undefined && val !== null) {
        map.set(val, item);
      }
    });

    const items = activeSelectedValues
      .map((val) => map.get(val))
      .filter(Boolean);

    const groupByCount = {};
    const groupsSet = new Set();
    let activeCount = 0;

    items.forEach((item) => {
      const groupName = extractItemProperty(item, groupKey, 'General');
      if (groupName) {
        groupsSet.add(groupName);
        groupByCount[groupName] = (groupByCount[groupName] || 0) + 1;
      }

      const status = extractItemProperty(item, statusKey);
      if (status === 'True' || status === true || status === 'active' || status === 1) {
        activeCount += 1;
      }
    });

    const itemsById = {};
    items.forEach((item) => {
      const val = extractItemProperty(item, valueKey);
      itemsById[val] = {
        raw: item,
        label: extractItemProperty(item, labelKey),
        group: extractItemProperty(item, groupKey),
        status: extractItemProperty(item, statusKey),
        customMetadata: metadataKeysList.reduce((acc, k) => {
          acc[k] = item[k];
          return acc;
        }, {})
      };
    });

    const computedMetadata = {
      count: items.length,
      selectedIds: activeSelectedValues,
      selectedItems: items,
      groups: Array.from(groupsSet),
      groupByCount,
      activeCount,
      inactiveCount: items.length - activeCount,
      itemsById
    };

    return {
      selectedItemObjects: items,
      selectionMetadata: computedMetadata,
      optionsByIdMap: map
    };
  }, [dataOptions, activeSelectedValues, valueKey, labelKey, groupKey, statusKey, metadataKeysList]);

  const handleSelectionUpdate = (newSelectedValues) => {
    if (!isControlledSelection) {
      setInternalSelectedValues(newSelectedValues);
    }

    if (typeof triggerChangeCallback === 'function') {
      const newItems = newSelectedValues.map((val) => optionsByIdMap.get(val)).filter(Boolean);

      const groupByCount = {};
      const groupsSet = new Set();
      let activeCount = 0;
      const itemsById = {};

      newItems.forEach((item) => {
        const groupName = extractItemProperty(item, groupKey, 'General');
        if (groupName) {
          groupsSet.add(groupName);
          groupByCount[groupName] = (groupByCount[groupName] || 0) + 1;
        }

        const status = extractItemProperty(item, statusKey);
        if (status === 'True' || status === true || status === 'active' || status === 1) {
          activeCount += 1;
        }

        const val = extractItemProperty(item, valueKey);
        itemsById[val] = {
          raw: item,
          label: extractItemProperty(item, labelKey),
          group: extractItemProperty(item, groupKey),
          status: extractItemProperty(item, statusKey),
          customMetadata: metadataKeysList.reduce((acc, k) => {
            acc[k] = item[k];
            return acc;
          }, {})
        };
      });

      const updatedMetadata = {
        count: newItems.length,
        selectedIds: newSelectedValues,
        selectedItems: newItems,
        groups: Array.from(groupsSet),
        groupByCount,
        activeCount,
        inactiveCount: newItems.length - activeCount,
        itemsById
      };

      triggerChangeCallback(newSelectedValues, newItems, updatedMetadata);
    }
  };

  const toggleOptionSelection = (optionValue) => {
    if (!isMultiSelection) {
      const updated = activeSelectedValues.includes(optionValue) ? [] : [optionValue];
      handleSelectionUpdate(updated);
      setIsDropdownOpen(false);
      return;
    }

    if (activeSelectedValues.includes(optionValue)) {
      handleSelectionUpdate(activeSelectedValues.filter((val) => val !== optionValue));
    } else {
      handleSelectionUpdate([...activeSelectedValues, optionValue]);
    }
  };

  const selectAllOptions = () => {
    const allValues = dataOptions.map((item) => extractItemProperty(item, valueKey)).filter(Boolean);
    handleSelectionUpdate(allValues);
  };

  const clearAllSelections = () => {
    handleSelectionUpdate([]);
  };

  const filteredOptionsList = useMemo(() => {
    if (!searchQuery.trim()) return dataOptions;
    const term = searchQuery.toLowerCase();
    return dataOptions.filter((item) => {
      const label = String(extractItemProperty(item, labelKey, '')).toLowerCase();
      const group = String(extractItemProperty(item, groupKey, '')).toLowerCase();
      return label.includes(term) || group.includes(term);
    });
  }, [dataOptions, searchQuery, labelKey, groupKey]);

  return (
    <div className={`space-y-3 font-sans ${className}`}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-gray-50 p-3 rounded-lg border border-gray-200" ref={containerRef}>
        <div className="flex items-center gap-3 flex-1">
          {isSearchEnabled && (
            <div className="relative flex-1 max-w-sm">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearchInputChange}
                placeholder={inputPlaceholder}
                className="w-full rounded-md border border-gray-300 bg-white pl-9 pr-7 py-2 text-xs text-gray-900 placeholder-gray-400 focus:border-[#3B74BE] focus:outline-none focus:ring-1 focus:ring-[#3B74BE] shadow-2xs"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={handleClearSearchInput}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <FaTimes className="text-xs" />
                </button>
              )}
            </div>
          )}

          <div className="relative">
            <button
              type="button"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className={`inline-flex items-center gap-2.5 rounded-md border px-3.5 py-2 text-xs font-semibold transition-colors shadow-2xs focus:outline-none focus:ring-2 focus:ring-[#3B74BE]/20 cursor-pointer ${
                activeSelectedValues.length > 0
                  ? 'border-[#3B74BE] bg-blue-50 text-[#3B74BE]'
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <FaFilter className={activeSelectedValues.length > 0 ? 'text-[#3B74BE]' : 'text-gray-400'} />
              <span>{displayTitle}</span>
              {activeSelectedValues.length > 0 && (
                <span className="inline-flex items-center justify-center rounded-full bg-[#3B74BE] px-2 py-0.5 text-[10px] font-bold text-white">
                  {activeSelectedValues.length}
                </span>
              )}
              <FaChevronDown
                className={`h-3 w-3 text-gray-400 transition-transform duration-200 ${
                  isDropdownOpen ? 'rotate-180 text-[#3B74BE]' : ''
                }`}
              />
            </button>

            {isDropdownOpen && (
              <div className="absolute right-0 sm:left-0 sm:right-auto z-50 mt-1.5 w-72 md:w-80 rounded-md border border-gray-200 bg-white shadow-lg ring-1 ring-black/5">
                <div className="p-2.5 border-b border-gray-200 bg-gray-50 rounded-t-md flex items-center justify-between">
                  <span className="text-xs font-semibold text-gray-700">
                    Select Options ({filteredOptionsList.length})
                  </span>
                  {isMultiSelection && (
                    <div className="flex items-center gap-2 text-xs">
                      <button
                        type="button"
                        onClick={selectAllOptions}
                        className="text-[#3B74BE] font-medium hover:underline cursor-pointer"
                      >
                        Select All
                      </button>
                      <span className="text-gray-300">|</span>
                      <button
                        type="button"
                        onClick={clearAllSelections}
                        className="text-gray-500 font-medium hover:text-red-600 hover:underline cursor-pointer"
                      >
                        Reset
                      </button>
                    </div>
                  )}
                </div>

                <div className="max-h-60 overflow-y-auto p-1 divide-y divide-gray-100">
                  {filteredOptionsList.length === 0 ? (
                    <div className="p-4 text-center text-xs text-gray-500">
                      No matching options found.
                    </div>
                  ) : (
                    filteredOptionsList.map((item) => {
                      const val = extractItemProperty(item, valueKey);
                      const label = extractItemProperty(item, labelKey);
                      const isChecked = activeSelectedValues.includes(val);

                      return (
                        <div
                          key={val}
                          onClick={() => toggleOptionSelection(val)}
                          className={`flex items-center gap-2.5 px-3 py-2 text-xs rounded-md cursor-pointer transition-colors ${
                            isChecked ? 'bg-blue-50 text-[#3B74BE] font-medium' : 'hover:bg-gray-50 text-gray-700'
                          }`}
                        >
                          <div
                            className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors ${
                              isChecked
                                ? 'border-[#3B74BE] bg-[#3B74BE] text-white'
                                : 'border-gray-300 bg-white'
                            }`}
                          >
                            {isChecked && <FaCheck className="text-[10px]" />}
                          </div>
                          <span className="truncate">{label}</span>
                        </div>
                      );
                    })
                  )}
                </div>

                <div className="flex items-center justify-between border-t border-gray-200 px-3 py-2 bg-gray-50 rounded-b-md text-xs">
                  <span className="text-gray-500">
                    Selected: <strong className="text-gray-900">{selectionMetadata.count}</strong>
                  </span>
                  <button
                    type="button"
                    onClick={() => setIsDropdownOpen(false)}
                    className="rounded-md bg-[#3B74BE] px-3 py-1 text-xs font-medium text-white hover:bg-[#3263a4] transition-colors cursor-pointer"
                  >
                    Done
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Optional Sort Component rendered in the same row */}
          {sortOptions.length > 0 && (
            <DataSort
              options={sortOptions}
              sortLabel={sortLabel}
              sortValue={controlledSortValue}
              defaultSortValue={defaultSortValue}
              sortOrder={controlledSortOrder}
              defaultSortOrder={defaultSortOrder}
              onSortChange={onSortChange}
              showSortOrderToggle={showSortOrderToggle}
            />
          )}
        </div>

        <div className="flex items-center gap-2 self-end sm:self-auto">
          {/* Optional Reset / Clear Filters button */}
          {(showReset || activeSelectedValues.length > 0) && (
            <button
              type="button"
              onClick={() => {
                clearAllSelections();
                if (typeof onReset === 'function') {
                  onReset();
                }
              }}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50 hover:text-red-600 transition-colors shadow-2xs cursor-pointer"
            >
              <FaTrashAlt className="text-[10px]" />
              <span>{showReset ? 'Reset' : 'Clear Filters'}</span>
            </button>
          )}
        </div>
      </div>

      {renderChips && selectedItemObjects.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 pt-1">
          <span className="text-xs font-semibold text-gray-400 mr-1">Active:</span>
          {selectedItemObjects.map((item) => {
            const val = extractItemProperty(item, valueKey);
            const label = extractItemProperty(item, labelKey);

            return (
              <span
                key={val}
                className="inline-flex items-center gap-1.5 rounded-md border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-medium text-[#3B74BE]"
              >
                <span>{label}</span>
                <button
                  type="button"
                  onClick={() => toggleOptionSelection(val)}
                  className="rounded-full p-0.5 hover:bg-blue-200 text-[#3B74BE] transition-colors focus:outline-none cursor-pointer"
                >
                  <FaTimes className="h-2.5 w-2.5" />
                </button>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
};

DataFilter.propTypes = {
  filterLabel: PropTypes.string,
  title: PropTypes.string,
  options: PropTypes.array,
  data: PropTypes.array,
  valueKey: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  labelKey: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  groupKey: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  statusKey: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  selectedValues: PropTypes.array,
  defaultSelectedValues: PropTypes.array,
  onSelectionChange: PropTypes.func,
  onChange: PropTypes.func,
  onSearchChange: PropTypes.func,
  onSearch: PropTypes.func,
  isMultiSelect: PropTypes.bool,
  isMulti: PropTypes.bool,
  isSearchable: PropTypes.bool,
  searchable: PropTypes.bool,
  searchPlaceholder: PropTypes.string,
  placeholder: PropTypes.string,
  showSelectedChips: PropTypes.bool,
  showChips: PropTypes.bool,
  className: PropTypes.string,
  extraMetadataKeys: PropTypes.array,
  metaExtraKeys: PropTypes.array,
  // Sort props
  sortOptions: PropTypes.array,
  sortLabel: PropTypes.string,
  sortValue: PropTypes.string,
  defaultSortValue: PropTypes.string,
  sortOrder: PropTypes.oneOf(['asc', 'desc']),
  defaultSortOrder: PropTypes.oneOf(['asc', 'desc']),
  onSortChange: PropTypes.func,
  showSortOrderToggle: PropTypes.bool,
  // Reset props
  showReset: PropTypes.bool,
  onReset: PropTypes.func
};

export default DataFilter;
